import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import imgAdmin from "@/assets/portal-admin.jpeg";
import imgTeacher from "@/assets/portal-teacher.jpeg";
import imgStudent from "@/assets/portal-student.jpeg";

const EASE = [0.32, 0.72, 0, 1] as const;

// ============ Portal mockups ============

function AdminMockup() {
  return (
    <div className="flex min-h-[320px]">
      <div className="w-12 bg-slate-950 p-2 space-y-2 shrink-0">
        <div className="size-8 rounded-lg bg-white/10" />
        <div className="pt-3 space-y-2">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className={cn("h-1.5 rounded-full", i === 0 ? "bg-white/30 w-7" : "bg-white/[0.06] w-5")} />
          ))}
        </div>
      </div>
      <div className="flex-1 p-4 bg-slate-50 dark:bg-slate-950/50">
        <div className="flex items-center justify-between mb-3">
          <div className="h-2 w-24 rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="size-6 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600" />
        </div>
        <div className="grid grid-cols-3 gap-2 mb-3">
          {[
            { v: "247", l: "Học viên", c: "bg-blue-500" },
            { v: "18", l: "Lớp học", c: "bg-emerald-500" },
            { v: "94%", l: "Chuyên cần", c: "bg-violet-500" },
          ].map((s, i) => (
            <div key={i} className="p-2 rounded-lg bg-white dark:bg-slate-900 ring-1 ring-black/[0.04] dark:ring-white/10 shadow-sm">
              <div className={`h-0.5 w-5 rounded-full ${s.c} mb-1.5`} />
              <div className="font-display text-base font-bold text-slate-900 dark:text-white">{s.v}</div>
              <div className="text-[8px] text-slate-400 dark:text-slate-500">{s.l}</div>
            </div>
          ))}
        </div>
        <div className="p-2.5 rounded-lg bg-white dark:bg-slate-900 ring-1 ring-black/[0.04] dark:ring-white/10 shadow-sm">
          <div className="h-1 w-14 rounded-full bg-slate-200 dark:bg-slate-800 mb-2" />
          <div className="flex items-end gap-1 h-12">
            {[40, 65, 45, 80, 55, 70, 90, 60, 75, 85, 95].map((h, i) => (
              <div key={i} className="flex-1 rounded-t bg-blue-500/20" style={{ height: `${h}%` }}>
                <div className="w-full rounded-t bg-blue-500" style={{ height: `${Math.min(h, 70)}%` }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function TeacherMockup() {
  return (
    <div className="flex min-h-[320px]">
      <div className="w-12 bg-slate-950 shrink-0" />
      <div className="flex-1 p-4 bg-slate-50 dark:bg-slate-950/50">
        <div className="h-2 w-24 rounded-full bg-slate-200 dark:bg-slate-800 mb-3" />
        <div className="grid grid-cols-5 gap-1.5">
          {["T2", "T3", "T4", "T5", "T6"].map((d) => (
            <div key={d} className="text-center text-[8px] text-slate-400 dark:text-slate-500 font-medium pb-1">{d}</div>
          ))}
          {[
            { c: "bg-blue-100 border-blue-300", t: "Toán 10A", time: "8:00" },
            null,
            { c: "bg-emerald-100 border-emerald-300", t: "Toán 11B", time: "9:30" },
            null,
            { c: "bg-violet-100 border-violet-300", t: "Toán 12A", time: "8:00" },
            null,
            { c: "bg-amber-100 border-amber-300", t: "Toán 10B", time: "14:00" },
            null,
            { c: "bg-blue-100 border-blue-300", t: "Toán 10A", time: "8:00" },
            null,
          ].map((slot, i) => (
            <div key={i} className={cn("rounded-md p-1 text-[8px] min-h-[36px]", slot ? `${slot.c} border` : "")}>
              {slot && (
                <>
                  <div className="font-medium text-slate-700 dark:text-slate-200 truncate">{slot.t}</div>
                  <div className="text-slate-400 dark:text-slate-500 mt-0.5">{slot.time}</div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StudentMockup() {
  return (
    <div className="flex min-h-[320px]">
      <div className="w-12 bg-slate-950 shrink-0" />
      <div className="flex-1 p-4 bg-slate-50 dark:bg-slate-950/50">
        <div className="h-2 w-24 rounded-full bg-slate-200 dark:bg-slate-800 mb-3" />
        {[
          { s: "Toán", t: "Bài tập chương 5", due: "Còn 2 ngày", done: false, c: "border-l-blue-500" },
          { s: "Lý", t: "Thí nghiệm quang học", due: "Còn 5 ngày", done: false, c: "border-l-emerald-500" },
          { s: "Hoá", t: "Bài kiểm tra giữa kỳ", due: "Đã nộp", done: true, c: "border-l-violet-500" },
          { s: "Anh", t: "Speaking Part 2", due: "Còn 7 ngày", done: false, c: "border-l-amber-500" },
        ].map((a, i) => (
          <div key={i} className={cn("p-2.5 rounded-lg bg-white dark:bg-slate-900 ring-1 ring-black/[0.04] dark:ring-white/10 shadow-sm mb-2 border-l-[3px]", a.c)}>
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-[8px] text-slate-400 dark:text-slate-500 uppercase tracking-wider">{a.s}</div>
                <div className="text-[10px] font-medium text-slate-700 dark:text-slate-200 mt-0.5 truncate">{a.t}</div>
              </div>
              <div className={cn("text-[8px] px-2 py-0.5 rounded-full font-medium shrink-0 ml-2",
                a.done ? "bg-emerald-100 text-emerald-700 dark:text-emerald-300" : "bg-amber-100 text-amber-700 dark:text-amber-300"
              )}>{a.due}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const PORTALS = [
  {
    role: "Admin",
    subtitle: "Quản lý toàn diện",
    desc: "Dashboard, quản lý học viên, lớp học, tài chính, báo cáo — tất cả trong tầm tay.",
    mockup: <AdminMockup />,
    image: imgAdmin,
    gradient: "from-blue-600 to-indigo-600",
  },
  {
    role: "Giáo viên",
    subtitle: "Tập trung dạy học",
    desc: "Lịch dạy, điểm danh, giao bài tập, theo dõi tiến độ học viên.",
    mockup: <TeacherMockup />,
    image: imgTeacher,
    gradient: "from-emerald-600 to-teal-600",
  },
  {
    role: "Học sinh",
    subtitle: "Học tập hiệu quả",
    desc: "Xem lịch học, bài tập, điểm danh, thông báo từ trung tâm.",
    mockup: <StudentMockup />,
    image: imgStudent,
    gradient: "from-violet-600 to-purple-600",
  },
];

export function LandingPortals() {
  return (
    <section id="portals" className="py-24 md:py-28 px-4 bg-slate-50 dark:bg-slate-950/60">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-flex px-3.5 py-1.5 rounded-full bg-indigo-50 dark:bg-indigo-500/10 ring-1 ring-indigo-100 text-[11px] uppercase tracking-[0.15em] font-medium text-indigo-700 dark:text-indigo-300 mb-4">
            3 cổng riêng biệt
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
            Mỗi vai trò, một giao diện riêng
          </h2>
        </div>

        {PORTALS.map((portal, i) => (
          <motion.div
            key={portal.role}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ delay: i * 0.1, duration: 0.7, ease: EASE }}
            className="grid lg:grid-cols-2 gap-10 items-center mb-20 last:mb-0"
          >
            <div className={cn(i % 2 === 1 && "lg:order-2")}>
              <div className={cn("inline-flex px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.15em] font-medium text-white mb-4 bg-gradient-to-r", portal.gradient)}>
                {portal.role}
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">{portal.subtitle}</h3>
              <p className="text-slate-500 dark:text-slate-400 mt-3 max-w-md leading-relaxed">{portal.desc}</p>

              <div className="mt-6 p-1 rounded-2xl bg-black/[0.02] dark:bg-white/5 ring-1 ring-black/[0.04] dark:ring-white/10 inline-block">
                <img
                  src={portal.image}
                  alt={`Giao diện cổng ${portal.role} Ezyclass — ${portal.subtitle}`}
                  loading="lazy"
                  className="rounded-[calc(1rem-4px)] w-52 h-36 object-cover"
                />
              </div>

            </div>

            <div className={cn(i % 2 === 1 && "lg:order-1")}>
              <div className="p-2 rounded-[2rem] bg-black/[0.03] dark:bg-white/5 ring-1 ring-black/[0.04] dark:ring-white/10">
                <div className="rounded-[calc(2rem-8px)] bg-white dark:bg-slate-900 shadow-[0_16px_60px_rgb(0,0,0,0.06)] overflow-hidden">
                  {portal.mockup}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
